import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RootState } from '../../app/store';
import { BLOCKED_PERIOD_TYPE, LOADING_STATUS } from '../../common/constants';
import blockedPeriodsService from '../../common/api/blockedPeriodsService';

export interface BlockedPeriodDetails {
  id?: string;
  accommodationId: string;
  startDate: Date;
  endDate: Date;
  type: string;
  description?: string;
  isCopyable: boolean;
  iCalExternalUid?: string;
  iCalFullDescription?: string;
  iCalLocation?: string;
  iCalImportId?: string;
  iCalDistributionChannelName?: string;
  adults?: number;
  children?: number;
  ownerBookingNotes?: string;
  checkInTime?: string;
  checkOutTime?: string;
}

interface BlockedPeriodDetailsState {
  details: BlockedPeriodDetails | null;
  status: LOADING_STATUS.IDLE | LOADING_STATUS.LOADING | LOADING_STATUS.FAILED;
}

export const initialBlockedPeriod: BlockedPeriodDetails = {
  accommodationId: undefined,
  startDate: undefined,
  endDate: undefined,
  type: BLOCKED_PERIOD_TYPE.OwnUse,
  isCopyable: false,
};

export const initialState: BlockedPeriodDetailsState = {
  details: null,
  status: LOADING_STATUS.IDLE,
};

export const getBlockedPeriodDetails = createAsyncThunk(
  'blockedPeriodDetails/getBlockedPeriodDetails',
  async (blockedPeriodId: string, { rejectWithValue }) => {
    try {
      return await blockedPeriodsService.getBlockedPeriodDetails(
        blockedPeriodId,
      );
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const saveOrUpdateBlockedPeriod = createAsyncThunk(
  'blockedPeriodDetails/saveOrUpdateBlockedPeriod',
  async (request: BlockedPeriodDetails, { rejectWithValue }) => {
    try {
      return await blockedPeriodsService.saveOrUpdateBlockedPeriod(request);
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

const blockedPeriodDetailsSlice = createSlice({
  name: 'blockedPeriodDetails',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getBlockedPeriodDetails.pending, state => {
        state.status = LOADING_STATUS.LOADING;
      })
      .addCase(getBlockedPeriodDetails.fulfilled, (state, action) => {
        state.status = LOADING_STATUS.IDLE;
        state.details = action.payload;
      })
      .addCase(getBlockedPeriodDetails.rejected, state => {
        state.status = LOADING_STATUS.FAILED;
      });
  },
});

export const selectBlockedPeriodDetails = (state: RootState) =>
  state.blockedPeriod.details;

export const selectBlockedPeriodDetailsStatus = (state: RootState) =>
  state.blockedPeriod.status;

export default blockedPeriodDetailsSlice.reducer;
